import { useQuery } from "@apollo/client/react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@athena/ui";
import { OrganizationOptionsDocument } from "@/graphql/organizations.generated";
import { flattenOrgOptions } from "@/lib/organizationOptions";

/**
 * Indentation per nesting level, as classes rather than an inline `paddingLeft` so the step
 * stays on the spacing scale. `SelectItem` already carries `pl-8` for the check indicator, and
 * `cn`'s tailwind-merge resolves the conflict in favor of these.
 *
 * Clamped at three levels: deeper than that the indent eats the name, and the hierarchy is
 * already legible from the rows above.
 */
const INDENT_CLASSES = [
    "",
    "pl-12",
    "pl-16",
    "pl-20",
] as const;

export interface OrganizationSelectOption {
    value: string;
    label: string;
}

interface OrganizationSelectProps {
    value: string | undefined;
    onValueChange: (value: string) => void;
    /** Shown when nothing is selected. */
    placeholder?: string;
    /**
     * Rows offered above the organizations — an "All organizations" filter entry, a "No
     * organization" choice. They are the caller's business: a filter and a form mean different
     * things by an empty selection, and only the caller knows which its value belongs to.
     */
    leadingOptions?: OrganizationSelectOption[];
    /**
     * Organizations to leave out — the integrations picker offers only orgs that do not already
     * have one. Applied before nesting, so a child whose excluded parent is gone rises to the top
     * level rather than sitting indented under nothing (see `flattenOrgOptions`).
     */
    excludeIds?: string[];
    /** Trigger passthrough, so callers can size and label their own control. */
    id?: string;
    className?: string;
    "aria-label"?: string;
    "aria-invalid"?: boolean;
    disabled?: boolean;
}

/**
 * The organization picker, everywhere one is offered.
 *
 * It exists because there were nine of these and they had drifted into three renderings of the
 * same list: three indented sub-organizations with an inline `paddingLeft` and an arrow, three
 * with non-breaking spaces in the row's text, and three — the video library's filter, the
 * integrations picker and the course editor's reassignment dropdown — rendered every
 * organization flat and in whatever order the server returned, so a sub-organization was
 * indistinguishable from a top-level one.
 *
 * `flattenOrgOptions` does the nesting and the alphabetizing at each level; this component owns
 * the query and the one rendering. The indent goes on the row rather than into its text, so the
 * trigger shows the plain organization name — the triggers here are narrow (`w-48`, `w-56`) and
 * a selected sub-org used to spend that width on padding and an arrow.
 */
export function OrganizationSelect({
    value,
    onValueChange,
    placeholder,
    leadingOptions = [],
    excludeIds,
    id,
    className,
    disabled,
    "aria-label": ariaLabel,
    "aria-invalid": ariaInvalid,
}: OrganizationSelectProps) {
    const { data } = useQuery(OrganizationOptionsDocument);
    const excluded = new Set(excludeIds ?? []);
    const options = flattenOrgOptions((data?.organizations ?? []).filter((org) => !excluded.has(org.id)));

    return (
        <Select value={value} onValueChange={onValueChange} disabled={disabled}>
            <SelectTrigger id={id} className={className} aria-label={ariaLabel} aria-invalid={ariaInvalid}>
                <SelectValue placeholder={placeholder} />
            </SelectTrigger>
            <SelectContent className="max-h-92 overflow-y-auto">
                {leadingOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                        {option.label}
                    </SelectItem>
                ))}
                {options.map(({ org, depth }) => (
                    <SelectItem
                        key={org.id}
                        value={org.id}
                        className={INDENT_CLASSES[Math.min(depth, INDENT_CLASSES.length - 1)]}
                    >
                        {org.name}
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    );
}
